import PropTypes from 'prop-types';
import React, {RefObject, Dispatch} from 'react';
import {importReactNativeWebView, isReactNative} from './utils';
import {clearMessages} from './FrameActions';
import {FrameState} from './FrameReducer';
import {FrameAction} from './FrameAction';

/**
 * Reference na WebView komponentu v případě, že jsme v React Native.
 *
 * @ignore
 */
let WebView: any;

if (isReactNative) {
    importReactNativeWebView()
        .then(RNWV => {
            WebView = RNWV.WebView;
        })
        .catch(error => {
            try {
                console.log('Failed to import WebView from react-native-webview', error);
            } catch (e) {
                // Ignorujeme když už nejde console.log nic nám nepomůže
            }
        });
}

/**
 * Příznak zda je nutné zprávy přenášet jako string.
 *
 * @ignore
 */
const isStringMessages = isReactNative || navigator.userAgent.indexOf('MSIE 9.0') >= 0;

/**
 * Properties frame.
 */
export interface FrameProps {
    /**
     * Stav frame.
     */
    state: FrameState;

    /**
     * Dispatcher akcí pro frame.
     */
    dispatch: Dispatch<FrameAction>;

    /**
     * URL webové stránky pro zobrazení ve frame.
     */
    src: string;

    /**
     * Titulek frame pro přístupnost.
     * Použito pouze v prostředí webového prohlížeče.
     */
    title?: string;

    /**
     * Zda má webová stránka uvnitř frame povolenu geolokaci.
     */
    geolocationEnabled?: boolean;

    /**
     * Callback pro přijetí zprávy od webové stránky uvnitř frame.
     *
     * @param message - Zpráva.
     */
    onMessage?: (message: any) => void;
}

/**
 * Frame pro zobrazení webové stránky.
 *
 * - V prohlížeči představuje iframe.
 * - V React Native představuje WebView.
 *
 * @param props - Properties komponenty.
 * @returns Element.
 */
export class Frame extends React.PureComponent<FrameProps> {
    /**
     * Reference na interní low-level frame komponentu.
     */
    private frame?: RefObject<HTMLIFrameElement>;

    /**
     * Konstuktor.
     *
     * @param props - Výchozí properties.
     */
    constructor(props) {
        super(props);
        this.frame = React.createRef();
    }

    setFrame = frame => {
        this.frame = frame;
    };

    /**
     * Pošle zprávu webové stránce uvnitř frame.
     *
     * @param data - Data zprávy.
     */
    private postMessage(data: any): void {
        const serializedData = isStringMessages ? JSON.stringify(data) : data;
        if (this.frame) {
            if (isReactNative) {
                (this.frame as any).postMessage(serializedData, '*');
            } else if (this.frame.current) {
                this.frame.current.contentWindow!.postMessage(serializedData, '*');
            } else {
                throw new Error('Frame found, but unable to send message');
            }
        } else {
            throw new Error('Frame not found');
        }
    }

    /**
     * Zpracuje zprávu od webové stránky ve frame.
     *
     * @param event - Událost se zprávou.
     */
    private handleMessage = (event: MessageEvent): void => {
        const {src, onMessage} = this.props;
        try {
            // V prohlížeči musíme kontrolovat origin, protože zráva mohla přijít i od jiného window.
            if (onMessage && (isReactNative || (src && src.toLowerCase().startsWith(event.origin.toLowerCase())))) {
                if (isReactNative) {
                    event = (event as any).nativeEvent; // React native má nativní event obalen
                }
                try {
                    const message = isStringMessages ? JSON.parse(event.data) : event.data;
                    try {
                        onMessage(message);
                    } catch (err) {
                        // TODO @compel Vyřešit, aby se někam logovalo a nepadalo to
                        //console.error("[Frame] Error calling onMessage function", err, "\nInput data", data);
                    }
                } catch (err) {
                    // TODO @compel Vyřešit, aby se někam logovalo a nepadalo to
                    //console.error("[Frame] Error in parsing json or calling onMessage function", err, "\nOriginal event", event);
                }
            }
        } catch (err) {
            // TODO @compel Vyřešit, aby se někam logovalo a nepadalo to
            //console.error("[Frame] Error process handleMessage function", err, "\nOriginal event", event);
        }
    };

    /**
     * Zpracuje zprávy na odeslání ve frontě, pokud je potřeba.
     */
    private processMessagesIfNeeded(): void {
        const {
            state: {postSuspended, messages},
        } = this.props;
        if (!postSuspended && messages && messages.length > 0) {
            messages.forEach(this.postMessage, this);
            this.onPostDone();
        }
    }

    private onPostDone(): void {
        this.props.dispatch(clearMessages());
    }

    /**
     * Voláno po připojení komponenty.
     */
    componentDidMount(): void {
        if (!isReactNative) {
            window.addEventListener('message', this.handleMessage);
        }
        this.processMessagesIfNeeded();
    }

    /**
     * Voláno po aktualizaci komponenty.
     */
    componentDidUpdate(): void {
        this.processMessagesIfNeeded();
    }

    /**
     * Voláno před odpojením komponenty.
     */
    componentWillUnmount(): void {
        if (!isReactNative) {
            window.removeEventListener('message', this.handleMessage);
        }
    }

    getAllow() {
        if (this.props.geolocationEnabled) {
            let allow = '';
            try {
                const url = new URL(this.props.src);
                allow = 'geolocation ' + url.origin;
            } catch (e) {
                allow = 'geolocation';
            }

            return allow;
        } else {
            return undefined;
        }
    }

    /**
     * Vykreslí komponentu.
     *
     * @returns Element.
     */
    render() {
        const {src, title, geolocationEnabled, state, onMessage, dispatch, ...otherProps} = this.props;

        if (isReactNative) {
            return (
                <WebView
                    ref={this.setFrame}
                    source={{uri: src}}
                    geolocationEnabled={geolocationEnabled}
                    onMessage={this.handleMessage}
                    {...otherProps}
                />
            );
        }

        return <iframe ref={this.frame} src={src} title={title} allow={this.getAllow()} {...otherProps} />;
    }

    /**
     * Typy properties.
     */
    static propTypes = {
        state: PropTypes.object.isRequired,
        dispatch: PropTypes.func.isRequired,
        src: PropTypes.string.isRequired,
        title: PropTypes.string,
        geolocationEnabled: PropTypes.bool,
        onMessage: PropTypes.func,
    };

    /**
     * Výchozí hodnoty properties.
     */
    static defaultProps = {
        geolocationEnabled: false,
    };
}
